// src/hooks/orderbooks/useLbankFutures.js
import { useEffect, useRef, useState } from "react";

const L = (...a) => console.log("[LBank FUTURES]", ...a);

// ровно тот же эндпоинт и те же хедеры, что у тебя в консоли
const DIRECT_ENDPOINT = "https://uuapi.rerrkvifj.com/cfd/market/v1.0/SendQryMarketOrder";

const FIXED_HEADERS = {
  "accept": "application/json, text/plain, */*",
  "accept-language": "en-US",
  "businessversioncode": "201",
  "content-encoding": "deflate; g-zip",
  "content-type": "application/json; charset=UTF-8",
  "ex-browser-name": "Chrome",
  "ex-browser-version": "141.0.0.0",
  "ex-client-source": "WEB",
  "ex-client-type": "WEB",
  "ex-currency": "USD-%24",
  "ex-device-id": "P083n5gGfTXdIh3hIWgICMRzQwGdTwy3",
  "ex-language": "en-US",
  "ex-language-zh": "%E8%8B%B1%E8%AF%AD",
  "ex-os-name": "Mac OS",
  "ex-os-version": "10.15.7",
  "ex-time-zone": "UTC+7",
  "ex-timestamp": "1760517718635",
  "ex-token": "d372ac0b1e1246e38704fa35d6d6858c",
  "ex-uid": "LBA1G59109",
  "priority": "u=1, i",
  "sec-ch-ua": "\"Google Chrome\";v=\"141\", \"Not?A_Brand\";v=\"8\", \"Chromium\";v=\"141\"",
  "sec-ch-ua-mobile": "?0",
  "sec-ch-ua-platform": "\"macOS\"",
  "sec-fetch-dest": "empty",
  "sec-fetch-mode": "cors",
  "sec-fetch-site": "cross-site",
  "source": "4",
  "versionflage": "true"
};

// нормализация ответа (таблица MarketOrder)
function normalizeDepthRows(rows) {
  if (!Array.isArray(rows)) return { asks: [], bids: [] };
  const asks = [];
  const bids = [];
  for (const it of rows) {
    const d = it?.data || it;
    const price = Number(d?.Price);
    const vol = Number(d?.Volume);
    const dir = String(d?.Direction ?? "");
    if (!Number.isFinite(price) || !Number.isFinite(vol)) continue;
    // эмпирика: Direction "1" — аски (sell), "0" — биды (buy)
    if (dir === "1") asks.push([price, vol]);
    else bids.push([price, vol]);
  }
  asks.sort((a, b) => a[0] - b[0]);
  bids.sort((a, b) => b[0] - a[0]);
  return { asks, bids };
}

function midFromBook(asks, bids) {
  const a = asks?.[0]?.[0];
  const b = bids?.[0]?.[0];
  if (Number.isFinite(a) && Number.isFinite(b)) return (a + b) / 2;
  if (Number.isFinite(a)) return a;
  if (Number.isFinite(b)) return b;
  return null;
}

/**
 * @param {string|null} symbol — "ETHUSDT", "LINKUSDT" и т.д.
 * @param {object} opts
 *   - depth (default 25)
 *   - pollIntervalMs (default 1000)
 */
export function useLbankFutures(symbol, opts = {}) {
  const {
    depth = 25,
    pollIntervalMs = 1000
  } = opts;

  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [tickerData, setTickerData] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const timerRef = useRef(null);

  const fetchDepthOnce = async () => {
    const body = {
      ProductGroup: "SwapU",
      ExchangeID: "Exchange",
      InstrumentID: symbol, // без дефисов, как у тебя: ETHUSDT / LINKUSDT
      Depth: depth
    };

    const req = {
      method: "POST",
      headers: FIXED_HEADERS,
      body: JSON.stringify(body),
      referrer: "https://www.lbank.com/",
      mode: "cors",
      credentials: "omit"
    };

    L("fetch depth", { url: DIRECT_ENDPOINT, instrument: symbol, depth });
    const res = await fetch(DIRECT_ENDPOINT, req);
    const json = await res.json();

    // у них бывает форма { success:false, error_code:10006, msg:"not open path" }
    if (json?.success === false) {
      throw new Error(json?.msg || "not open path");
    }
    const ok = json?.code === 200 || json?.success === true;
    const rows = Array.isArray(json?.data) ? json.data : null;
    if (!ok || !rows || rows.length === 0) {
      throw new Error(json?.msg || json?.message || "Empty futures orderbook");
    }

    const marketRows = rows.filter(
      (r) => (r?.table ?? "MarketOrder") === "MarketOrder" || r?.data
    );
    const { asks, bids } = normalizeDepthRows(marketRows);
    if (asks.length === 0 && bids.length === 0) {
      throw new Error("Empty futures orderbook");
    }

    setOrderBook({ asks, bids });
    const mid = midFromBook(asks, bids);
    setTickerData(mid != null ? { lastPrice: String(mid) } : null);
    setIsConnected(true);
    setError(null);
  };

  useEffect(() => {
    if (!symbol) {
      setOrderBook({ asks: [], bids: [] });
      setTickerData(null);
      setIsConnected(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        await fetchDepthOnce();
      } catch (e) {
        L("init error", e?.message || e);
        setError(e?.message || String(e));
        setIsConnected(false);
      }
      if (cancelled) return;

      const tick = async () => {
        try {
          await fetchDepthOnce();
        } catch (e) {
          L("poll error", e?.message || e);
          setIsConnected(false);
          setError(e?.message || String(e));
        }
      };
      timerRef.current = setInterval(tick, pollIntervalMs);
    };

    run();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, depth, pollIntervalMs]);

  return { isConnected, error, orderBook, tickerData };
}

export default useLbankFutures;
